import { Delete, Edit } from "@mui/icons-material";
import { Alert, Box, Button, Tooltip, Typography } from "@mui/material";
import { NoModificationIcon } from "../../ui/primitives/CustomIcons";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  listAssociatedUsers,
  removeAssociatedUsers,
} from "../../modules/project";
import { projectTeamHeader } from "../../static-data/users";
import TableComponent from "../../ui/data/table/TableComponent";
import ActionMenu from "../../ui/primitives/ActionMenu";
import ConfirmationModal from "../../ui/primitives/ConfirmationModal";
import SearchBar from "../../ui/primitives/SearchBar";
import TeamTableAction from "./TeamTableAction";
import { useUser } from "../../context/UserContext";
import { useAccess } from "../../hooks/useAccess";
import useDebounce from "../../hooks/useDebounce";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const ProjectUsersList = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState("desc");
  const { projectId } = useParams();
  const [userToRemove, setUserToRemove] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [mode, setMode] = useState("add");

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  const debouncedSearch = useDebounce(search, 500);

  const { currentUser } = useUser();
  const { canAccess } = useAccess();

  const canManageUsers = canAccess("ManageProjectUsers");
  const currentUserId = currentUser?.id;

  const fetchUsersData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listAssociatedUsers(projectId, {
        page: page + 1,
        perPage: rowsPerPage,
        search: debouncedSearch || undefined,
      });
      setUsers(response.users || []);
      setTotalCount(response.count || 0);
    } catch (err) {
      setError("Failed to fetch users. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [projectId, page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    fetchUsersData();
  }, [fetchUsersData]);

  const tableData = useMemo(
    () =>
      users?.map((row) => ({
        ...row,
        name: row.user?.full_name,
        email: row.user?.email,
        status: row.user?.status,
        role: row.role_details?.name || row.role,
        isOrgScoped: row.role_details?.scope === "org",
      })),
    [users],
  );

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(0);
  };

  const handleDeleteClick = (user) => {
    setOpenDelete(true);
    setUserToRemove(user);
  };

  const handleEditClick = (user) => {
    setOpenAdd(true);
    setUserToRemove(user);
    setMode("edit");
  };

  const handleDeleteCancel = () => {
    setOpenDelete(false);
    setUserToRemove(null);
    setError(null);
  };

  const handleDelete = async () => {
    try {
      await removeAssociatedUsers(userToRemove?.id);
      fetchUsersData();
      handleDeleteCancel();
    } catch (err) {
      setError(err.message || "Failed to update project. Please try again.");
    }
  };

    const canActOnRow = (row) =>
      canManageUsers &&
      !row.isOrgScoped &&
      row.user_id !== currentUserId;

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h4">Project Team</Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <SearchBar
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          clearSearch={() => handleSearchChange("")}
          placeholder="Search by name or email"
        />
        {canManageUsers && (
          <Button
            variant="contained"
            onClick={() => {
              setMode("add");
              setUserToRemove(null);
              setOpenAdd(true);
            }}
          >
            Manage Team
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableComponent
        data={tableData}
        headerTabs={projectTeamHeader}
        order={order}
        setOrder={setOrder}
        defaultSort="created_at"
        isLoading={loading}
        striped
        tablekey="team members"
        showPages
        showCount
        showRowCount
        page={page + 1}
        perPage={rowsPerPage}
        setPerPage={(e) => {
          setRowsPerPage(Number(e.target.value));
          setPage(0);
        }}
        totalCount={totalCount}
        handlePageChange={(newPage) => setPage(Number(newPage) - 1)}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        action={(row) => {
        if (row.isOrgScoped) {
          return (
            <Tooltip title="This is an organization-level role and cannot be modified here" arrow>
              <NoModificationIcon />
            </Tooltip>
          );
        }
        return canActOnRow(row) ? (
          <ActionMenu
            items={[
              {
                label: "Edit Role",
                icon: <Edit />,
                onClick: () => handleEditClick(row),
              },
              {
                label: "Remove",
                icon: <Delete />,
                color: "error",
                onClick: () => handleDeleteClick(row),
              },
            ]}
          />
        ) : null;
      }}
      />

      <ConfirmationModal
        show={openDelete}
        handleConfirm={handleDelete}
        handleClose={handleDeleteCancel}
        title="Remove User from Project"
        body="Are you sure you want to remove this user from the project?"
      />
      <TeamTableAction
        fetchUsersData={fetchUsersData}
        users={users}
        setOpenModal={setOpenAdd}
        open={openAdd}
        mode={mode}
        setMode={setMode}
        selectedUser={userToRemove}
      />
    </Box>
  );
};

export default ProjectUsersList;